import { gql, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Heading,
  Link,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { dinero } from 'dinero.js';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as queryString from 'query-string';
import * as React from 'react';

import { CurrencyDisplay } from '@/components/Currency';
import { Redraft } from '@/components/Redraft';
import { RawDraftContentStateFragmentAST } from '@/fragments/RawDraftContentStateFragment';
import { useDefaultCurrency } from '@/hooks/useDefaultCurrency';
import { useElementHeight } from '@/hooks/useElementHeight';
import { Card } from '@/imports/ui/chakra/components/Card';
import { Loading } from '@/imports/ui/chakra/components/Loading';
import { Prose } from '@/imports/ui/chakra/components/Prose';
import { AlignCenterIcon } from '@/imports/ui/chakra/feather';
import { Butterfly, ButterflyMain } from '@/layouts/Butterfly/Butterfly';

import {
  MembershipDetailsPage_MembershipType,
  MembershipDetailsPage_MembershipTypeVariables,
} from './graphql-types/MembershipDetailsPage_MembershipType';
import { MembershipDetailsPage_currentUser } from './graphql-types/MembershipDetailsPage_currentUser';

import { PrePurchaseConfirmationModal } from '../MembershipsPage/components/MembershipsPageSubmitFooter/components/PrePurchaseConfirmationModal';

export const MembershipDetailsPage: React.FC = () => {
  const footer = useElementHeight();

  const { currency } = useDefaultCurrency();
  const router = useRouter();
  const membershipTypeId = router.query.membershipTypeId.toString();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const { data, loading } = useQuery<
    MembershipDetailsPage_MembershipType,
    MembershipDetailsPage_MembershipTypeVariables
  >(GET_MEMBERSHIP, {
    variables: { input: { id: membershipTypeId } },
  });

  const { data: userData, loading: userDataLoading } =
    useQuery<MembershipDetailsPage_currentUser>(CURRENT_USER);

  const annualPrice = data?.membershipType.membershipType?.priceV2.annual
    ? dinero(data?.membershipType.membershipType?.priceV2.annual)
    : dinero({ amount: 0, currency });

  const monthlyPrice = data?.membershipType.membershipType?.priceV2.monthly
    ? dinero(data?.membershipType.membershipType?.priceV2.monthly)
    : dinero({ amount: 0, currency });

  const showPrePurchaseConfirmationModal =
    !!userData?.currentUser?.membershipData?.currentMembershipTerm;

  const hasDescription =
    !!data?.membershipType.membershipType?.description?.blocks.find(
      (block) => !!block.text.length
    );

  const redirectToCheckout = (membershipTypeId: string) => {
    if (!userData?.currentUser?._id) {
      return router.push(
        `/login?${queryString.stringify({
          isFromMembershipPage: true,
          redirect: `/new-membership/${membershipTypeId}`,
        })}`
      );
    }

    return router.push(`/new-membership/${membershipTypeId}`);
  };

  return (
    <>
      <Butterfly>
        <ButterflyMain>
          <Stack
            maxWidth="container.sm"
            mx="auto"
            mb={`${footer.height}px`}
            p="4"
          >
            <Stack spacing="2" align="flex-start">
              <Link as={NextLink} href="/memberships">
                Back to Memberships
              </Link>
              <Heading size="md">
                {data?.membershipType.membershipType?.name}
              </Heading>
            </Stack>

            <Stack>
              {loading ? (
                <Loading />
              ) : (
                <Card p="6" mt="8">
                  {hasDescription ? (
                    <Prose color="gray.600">
                      <Redraft
                        rawDraftContentState={
                          data?.membershipType.membershipType?.description
                        }
                      />
                    </Prose>
                  ) : (
                    <Stack align="center" spacing="2" justify="center" h="md">
                      <AlignCenterIcon color="gray.700" boxSize="5" />
                      <Text>
                        No description has been added for this membership
                      </Text>
                    </Stack>
                  )}
                </Card>
              )}
            </Stack>
          </Stack>
          <Box
            py="4"
            bg="white"
            borderTop="1px"
            borderTopColor="gray.100"
            position="fixed"
            bottom="0"
            left="0"
            right="0"
            ref={footer.ref}
            px="4"
          >
            <Stack
              direction="row"
              justify="space-between"
              align="center"
              maxWidth="container.sm"
              mx="auto"
            >
              {data?.membershipType.membershipType?.priceV2.monthly ? (
                <Stack spacing="0">
                  <CurrencyDisplay
                    textStyle="xlBodyStrong"
                    color="gray.600"
                    value={monthlyPrice}
                  />
                  <Text textStyle="detailStrong" color="gray.500">
                    BILLED MONTHLY
                  </Text>
                </Stack>
              ) : (
                <Stack spacing="0">
                  <CurrencyDisplay
                    textStyle="xlBodyStrong"
                    color="gray.600"
                    value={annualPrice}
                  />
                  <Text textStyle="detailStrong" color="gray.500">
                    BILLED ANNUALLY
                  </Text>
                </Stack>
              )}

              <Button
                isLoading={userDataLoading}
                onClick={
                  showPrePurchaseConfirmationModal
                    ? onOpen
                    : () => redirectToCheckout(membershipTypeId)
                }
              >
                {userData?.currentUser?._id ? 'Pay Now' : 'Checkout'}
              </Button>
            </Stack>
          </Box>
        </ButterflyMain>
      </Butterfly>

      <PrePurchaseConfirmationModal
        user={userData?.currentUser ?? undefined}
        isOpen={isOpen}
        onClose={onClose}
        membershipTypeId={membershipTypeId}
      />
    </>
  );
};

const GET_MEMBERSHIP = gql`
  query MembershipDetailsPage_MembershipType($input: MembershipTypeInput!) {
    membershipType(input: $input) {
      ... on MembershipTypeSuccess {
        membershipType {
          _id
          name
          priceV2 {
            annual
            monthly
          }
          description {
            ...RawDraftContentStateFragment
          }
        }
      }
    }
  }
  ${RawDraftContentStateFragmentAST}
`;

const CURRENT_USER = gql`
  query MembershipDetailsPage_currentUser {
    currentUser {
      _id
      membershipData {
        currentMembershipTerm {
          _id
        }
      }
      ...PrePurchaseConfirmationModal_UserFragment
    }
  }
  ${PrePurchaseConfirmationModal.userFragment}
`;
